import domReady from "@wordpress/dom-ready";
import { customFadeInTopElem } from "assets/animations";
import { debounce } from "@wordpress/compose";

const getAjaxFormData = (inputsParent) => {
	const ajaxFormData = new FormData();
	inputsParent.querySelectorAll("input")?.forEach((input) => {
		ajaxFormData.append(input.name, input.value);
	});
	inputsParent.querySelectorAll("select")?.forEach((select) => {
		ajaxFormData.append(select.name, select.value);
	});
	return ajaxFormData;
};

const loadMoreEvent = new CustomEvent("load-more", {
	detail: {
		message: "Called when content is loaded via AJAX",
	},
});

const animateFilters = (section) => {
	const menuItems = section.querySelectorAll("nav ul li");
	const parentElem = section.querySelector(".post-grid__content");
	const start = "top 90%";
	if (menuItems && menuItems.length > 0) {
		customFadeInTopElem({
			elem: menuItems,
			parentElem,
			start,
		});
	}
};

let postItemAnimations = [];
const animatePostItems = (section) => {
	const items = section.querySelectorAll(".post-grid__item:not(.animated)");

	let previousStart = null;
	let itemIndex = 0;
	const start = "top 90%";

	items?.forEach((item) => {
		if (!item.classList.contains("animated")) {
			const trigger = window.customScrollbarInstance?.ScrollTrigger?.create({
				trigger: item,
				start,
				once: true,
			});

			const currentStart = trigger.start;

			let delay = 0;

			if (currentStart === previousStart) {
				delay = 0.15 * itemIndex;
			} else {
				itemIndex = 0;
			}

			previousStart = currentStart;

			itemIndex++;

			postItemAnimations.push(
				window.customScrollbarInstance?.gsap?.fromTo(
					item,
					{
						y: "25px",
						opacity: 0,
					},
					{
						y: "0",
						opacity: 1,
						duration: 0.75,
						ease: "power2.inOut",
						delay,
						scrollTrigger: {
							trigger: item,
							start,
						},
						onComplete: () => {
							item?.classList?.add("animated");
						},
					},
				),
			);
		}
	});
};

const destroyAnimatePostItems = () => {
	postItemAnimations.forEach((anim, index) => {
		anim.kill();
		if (index === postItemAnimations.length - 1) {
			postItemAnimations = [];
		}
	});
};

const goToOnLoad = (section) => {
	const newItem = section?.querySelector(".post-grid__item:not(.animated)");

	if (newItem) {
		const offsetTop = (window.innerHeight - newItem.clientHeight) / 2;
		window.customScrollbarInstance?.scrollbarInstance?.scrollIntoView(newItem, {
			offsetTop,
		});
	}
};

const filtersHandlers = (section) => {
	const filterForm = section.querySelector("form.post-grid__filters");
	if (!filterForm) return false;
	const filterBtns = filterForm.querySelectorAll("nav ul li a");
	filterBtns?.forEach((btn) => {
		btn.addEventListener("click", () => {
			if (btn.closest("li").classList.contains("active")) return false;

			const filterBtnActive = filterForm.querySelector("nav ul li.active");
			filterBtnActive?.classList.remove("active");

			btn.closest("li").classList.add("active");

			filterForm.querySelector("input[name=term_id]").value =
				btn.getAttribute("data-id");

			const postGridSection = btn.closest(".wp-block-theme-blocks-post-grid");

			const ajaxFormData = getAjaxFormData(filterForm);

			fetch("/wp-admin/admin-ajax.php", {
				method: "POST",
				body: ajaxFormData,
			})
				// Converting to JSON
				.then((response) => response.json())

				// Displaying results to console
				.then((json) => {
					// console.log("json response", json);
					// console.log("html_return", json.html_return);

					// STOPS LOADING
					filterForm.classList.remove("loading");

					const postGridContentRow = postGridSection.querySelector(
						".post-grid__content .row",
					);

					postGridContentRow.innerHTML = "";

					if (json.html_return) {
						postGridContentRow.innerHTML = json.html_return;
					}
					if (json.query_vars) {
						const loadMoreButton = section.querySelector(".loadmore-btn");
						if (loadMoreButton) {
							const buttonWrapper = loadMoreButton.closest(".link-wrapper");
							if (json.max_page === 1) {
								buttonWrapper.classList.add("hide");
							} else {
								buttonWrapper.classList.remove("hide");
							}

							loadMoreButton.querySelector("input[name=query_vars]")["value"] =
								json.query_vars;
							loadMoreButton.querySelector("input[name=paged]")["value"] = 1;
							loadMoreButton.querySelector("input[name=max_page]")["value"] =
								json.max_page;
						}
						document.dispatchEvent(loadMoreEvent);
					}
				});
		});
	});
};

const loadMoreHandlers = (section) => {
	const loadMoreButton = section.querySelector(".loadmore-btn");

	loadMoreButton?.addEventListener("click", () => {
		if (loadMoreButton.classList.contains("disabled")) return false;

		const buttonWrapper = loadMoreButton.closest(".link-wrapper");
		const postGridSection = buttonWrapper.closest(
			".wp-block-theme-blocks-post-grid",
		);

		if (!postGridSection || !buttonWrapper) return false;

		const max_page = parseInt(
			loadMoreButton.querySelector("input[name=max_page]")["value"],
		);

		// STARTS LOADING
		loadMoreButton.classList.add("disabled");
		buttonWrapper.classList.add("loading");

		fetch("/wp-admin/admin-ajax.php", {
			method: "POST",
			body: getAjaxFormData(loadMoreButton),
		})
			// Converting to JSON
			.then((response) => response.json())

			// Displaying results to console
			.then((json) => {
				// console.log("json response", json);
				// console.log("html_return", json.html_return);

				// STOPS LOADING
				loadMoreButton.classList.remove("disabled");
				buttonWrapper.classList.remove("loading");

				if (parseInt(json.new_paged) === max_page) {
					buttonWrapper.classList.add("hide");
				} else {
					buttonWrapper.classList.remove("hide");
				}

				if (json.html_return) {
					loadMoreButton.querySelector("input[name=paged]")["value"] =
						json.new_paged;
					const postGridContentRow = postGridSection.querySelector(
						".post-grid__content .row",
					);
					postGridContentRow.innerHTML =
						postGridContentRow.innerHTML + json.html_return;

					goToOnLoad(postGridSection);

					document.dispatchEvent(loadMoreEvent);
				}
			});
	});
};

domReady(() => {
	const allPostGridSections = document.querySelectorAll(
		".wp-block-theme-blocks-post-grid",
	);

	allPostGridSections?.forEach((section) => {
		animateFilters(section);
		animatePostItems(section);
		loadMoreHandlers(section);
		filtersHandlers(section);
	});

	window.addEventListener(
		"load-more",
		(event) => {
			const allPostGridSections = document.querySelectorAll(
				".wp-block-theme-blocks-post-grid",
			);

			allPostGridSections?.forEach((section) => {
				animatePostItems(section);
			});
		},
		true,
	);
});

window.addEventListener(
	"resize",
	debounce(() => {
		destroyAnimatePostItems();
		const allPostGridSections = document.querySelectorAll(
			".wp-block-theme-blocks-post-grid",
		);

		allPostGridSections?.forEach((section) => {
			animatePostItems(section);
		});
	}, 50),
);
